import React, { useState, useEffect, useRef } from "react";
import { Button, TextField, Grid, Box, InputAdornment, IconButton } from "@mui/material";

import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { userService } from "../Context/Auth/AuthService";
import LoadingButton from "@mui/lab/LoadingButton";

// import { USER_REGEX, EMAIL_REGEX, PWD_REGEX } from './validation';

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{8,24}$/;

function SignupPage({ onSignUpSuccess }) {
   const [fullName, setFullName] = useState("");
   const [referralCode, setReferralCode] = useState("");
   const [email, setEmail] = useState("");
   const [password, setPassword] = useState("");
   const [confirmPassword, setConfirmPassword] = useState("");
   const [withdrawCode, setWithdrawCode] = useState("");
   const [showPassword, setShowPassword] = useState(false);
   const [validEmail, setValidEmail] = useState(false);
   const [validPassword, setValidPassword] = useState(false);
   const [passwordsMatch, setPasswordsMatch] = useState(false);
   const [formValid, setFormValid] = useState(false);
   const fullNameRef = useRef(null);
   const [referralCodeFocused, setReferralCodeFocused] = useState(false);
   const [withdrawCodeFocused, setWithdrawCodeFocused] = useState(false);
   const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
      fullNameRef.current.focus();
   }, []);

   useEffect(() => {
      setValidEmail(EMAIL_REGEX.test(email));
      setValidPassword(PWD_REGEX.test(password));
      setPasswordsMatch(password === confirmPassword && password !== "");
   }, [email, password, confirmPassword]);

   useEffect(() => {
      setFormValid(
         validEmail &&
            validPassword &&
            passwordsMatch &&
            fullName !== "" &&
            referralCode !== "" &&
            withdrawCode !== ""
      );
   }, [validEmail, validPassword, passwordsMatch, fullName, referralCode, withdrawCode]);

   const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formValid) {
         fullNameRef.current.focus();
         return;
      }

      const values = {
         userName: fullName,
         email,
         password,
         referralCode,
         withdrawCode,
      };

      try {
         setIsLoading(true);
         const response = await userService(values, false);
         onSignUpSuccess(response);
      } catch (error) {
         console.log(error.message);
      } finally {
         setFullName("");
         setEmail("");
         setPassword("");
         setConfirmPassword("");
         setReferralCode("");
         setWithdrawCode("");
         setIsLoading(false);
      }
   };

   return (
      <>
         <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
            <Grid container spacing={2}>
               <Grid item xs={12}>
                  <TextField
                     autoComplete="name"
                     name="fullName"
                     required
                     fullWidth
                     id="fullName"
                     label="Full Name"
                     value={fullName}
                     onChange={(e) => setFullName(e.target.value)}
                     inputRef={fullNameRef}
                     error={!fullName && formValid}
                     helperText={!fullName && formValid ? "Full Name is required" : ""}
                  />
               </Grid>
               <Grid item xs={12}>
                  <TextField
                     required
                     fullWidth
                     id="email"
                     label="Email Address"
                     name="email"
                     autoComplete="email"
                     value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     error={!validEmail && email}
                     helperText={!validEmail && email ? "Invalid email format" : ""}
                  />
               </Grid>
               <Grid item xs={12}>
                  <TextField
                     required
                     fullWidth
                     name="password"
                     label="Password"
                     type={showPassword ? "text" : "password"}
                     id="password"
                     value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     error={!validPassword && password}
                     helperText={
                        !validPassword && password
                           ? "Password must be at least 8 characters long and contain letters and numbers"
                           : ""
                     }
                     InputProps={{
                        endAdornment: (
                           <InputAdornment position="end">
                              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                 {showPassword ? <Visibility /> : <VisibilityOff />}
                              </IconButton>
                           </InputAdornment>
                        ),
                     }}
                  />
               </Grid>
               <Grid item xs={12}>
                  <TextField
                     required
                     fullWidth
                     name="confirmPassword"
                     label="Confirm Password"
                     type={showPassword ? "text" : "password"}
                     id="confirmPassword"
                     value={confirmPassword}
                     onChange={(e) => setConfirmPassword(e.target.value)}
                     error={!passwordsMatch && confirmPassword}
                     helperText={!passwordsMatch && confirmPassword ? "Passwords do not match" : ""}
                     InputProps={{
                        endAdornment: (
                           <InputAdornment position="end">
                              <IconButton edge="end">
                                 {confirmPassword ? (
                                    <>
                                       {" "}
                                       {passwordsMatch ? (
                                          <CheckIcon color="success" />
                                       ) : (
                                          <CloseIcon color="error" />
                                       )}
                                    </>
                                 ) : (
                                    ""
                                 )}
                              </IconButton>
                           </InputAdornment>
                        ),
                     }}
                  />
               </Grid>
               <Grid item xs={12}>
                  <TextField
                     autoComplete="referralCode"
                     name="referralCode"
                     required
                     fullWidth
                     id="referralCode"
                     label="Referral Code"
                     value={referralCode}
                     onChange={(e) => setReferralCode(e.target.value)}
                     error={!referralCode && formValid}
                     helperText={
                        referralCodeFocused
                           ? "Don't have a referral code? Contact your manager"
                           : !referralCode && formValid
                             ? "Referral code is required"
                             : ""
                     }
                     onFocus={() => setReferralCodeFocused(true)}
                     onBlur={() => setReferralCodeFocused(false)}
                  />
               </Grid>
               <Grid item xs={12}>
                  <TextField
                     autoComplete="withdrawCode"
                     name="withdrawCode"
                     required
                     fullWidth
                     id="withdrawCode"
                     label="Withdraw Code"
                     value={withdrawCode}
                     onChange={(e) => setWithdrawCode(e.target.value)}
                     error={!withdrawCode && formValid}
                     helperText={
                        withdrawCodeFocused
                           ? "Don't forget your withdraw code"
                           : !withdrawCode && formValid
                             ? "Withdraw code is required"
                             : ""
                     }
                     onFocus={() => setWithdrawCodeFocused(true)}
                     onBlur={() => setWithdrawCodeFocused(false)}
                  />
               </Grid>
            </Grid>
            <LoadingButton
               loading={isLoading}
               type="submit"
               fullWidth
               variant="contained"
               sx={{ mt: 3, mb: 2 }}
               disabled={!formValid}
            >
               Sign Up
            </LoadingButton>
         </Box>
      </>
   );
}

export default SignupPage;
